import { useCallback, useEffect, useRef } from "react";
import { environment } from "../../environment";
import { useLogin } from "../login";
import { pluginRequest } from "./api";
import { PluginApiResult, PluginSubscriptionStatusView } from "./types";

const subscriptionUrl = (): string => `${environment.pluginUrl}/subscription`;

// #region usePluginSubscription
/**
 * The signed-in brand's Shopify plugin subscription. The brand always comes
 * from the auth token server-side. POST starts the trial / subscribes / resumes
 * a subscription set to cancel; DELETE cancels at period end. All three return
 * the fresh status view.
 */
export const usePluginSubscription = () => {
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);
    useEffect(() => { loginFunctionsRef.current = loginFunctions; }, [loginFunctions]);

    const logout = useCallback(() => loginFunctionsRef.current.logout(), []);

    const getStatus = useCallback((): Promise<PluginApiResult<PluginSubscriptionStatusView>> =>
        pluginRequest<PluginSubscriptionStatusView>('GET', subscriptionUrl(), authToken, logout), [authToken, logout]);

    const subscribe = useCallback((): Promise<PluginApiResult<PluginSubscriptionStatusView>> =>
        pluginRequest<PluginSubscriptionStatusView>('POST', subscriptionUrl(), authToken, logout), [authToken, logout]);

    const cancel = useCallback((): Promise<PluginApiResult<PluginSubscriptionStatusView>> =>
        pluginRequest<PluginSubscriptionStatusView>('DELETE', subscriptionUrl(), authToken, logout), [authToken, logout]);

    return { getStatus, subscribe, cancel };
};
